import { TLocation } from "../components/paginas/Student/FlightLogs/FlightLogs";

/* eslint-disable no-console */
interface LandingInfo {
	startTime: Date;
	endTime: Date;
	startAltitude: number;
	startMslAltitude: number;
	endAltitude: number;
	endMslAltitude: number;
	duration: number;
	averageDescentRate: number;
	averageDescentRateFPM: number;
	location: {
		latitude: number;
		longitude: number;
	};
}

export class LandingDetector {
	private readonly ALTITUDE_THRESHOLD: number;
	private readonly TIME_THRESHOLD: number;
	private readonly DESCENT_RATE_THRESHOLD: number;
	private readonly MAX_DESCENT_RATE: number;
	private readonly DEBUG: boolean;
	private gpsData: TLocation[];
	private landings: LandingInfo[];
	private minAltitude: number;

	constructor(gpsData: TLocation[]) {
		this.ALTITUDE_THRESHOLD = 15; // meters
		this.TIME_THRESHOLD = 25; // seconds
		this.DESCENT_RATE_THRESHOLD = 0.83; // meters per second (~500 ft/min)
		this.MAX_DESCENT_RATE = 6; // meters per second (~1200 ft/min)
		this.DEBUG = false;
		this.gpsData = gpsData;
		this.minAltitude = Math.min(
			...gpsData.map((point) => point.mslAltitude ?? point.altitude),
		);
		this.landings = [];
	}

	private debug(message: string, data: any = null): void {
		if (this.DEBUG) {
			console.log("[DEBUG]", message);
			if (data) {
				console.log(JSON.stringify(data, null, 2));
			}
		}
	}

	public detectLandings(): LandingInfo[] {
		if (this.gpsData.length < 2) {
			this.debug("Insufficient data points");
			return this.landings;
		}

		let potentialLanding: any = null;
		let consecutiveDescents = 0;

		for (let i = 1; i < this.gpsData.length; i++) {
			const current = this.gpsData[i];
			const previous = this.gpsData[i - 1];

			const timeChange = (current.time - previous.time) / 1000; // in seconds
			const altitudeChange = previous.altitude - current.altitude;

			this.debug("Analyzing points:", {
				current: {
					time: new Date(current.time).toISOString(),
					altitude: current.altitude,
				},
				previous: {
					time: new Date(previous.time).toISOString(),
					altitude: previous.altitude,
				},
				timeChange,
				altitudeChange,
				descentRateFPM: (altitudeChange / timeChange) * 196.85, // ft/min for debugging
			});

			if (timeChange <= 0) {
				continue;
			}

			const descentRate = altitudeChange / timeChange;

			if (
				descentRate > this.DESCENT_RATE_THRESHOLD &&
				descentRate < this.MAX_DESCENT_RATE
			) {
				consecutiveDescents++;

				if (!potentialLanding) {
					potentialLanding = {
						startIndex: i - 1,
						startAltitude: previous.altitude,
						startMslAltitude: previous.mslAltitude ?? previous.altitude,
						startTime: previous.time,
					};
				}
			} else {
				if (potentialLanding && consecutiveDescents >= 3) {
					const totalAltitudeChange =
						potentialLanding.startAltitude - current.altitude;
					const totalTime = (current.time - potentialLanding.startTime) / 1000;

					if (
						totalAltitudeChange > this.ALTITUDE_THRESHOLD &&
						totalTime > this.TIME_THRESHOLD &&
						current.speed < 30
					) {
						if (
							(current.mslAltitude ?? current.altitude) - this.minAltitude >
							30
						) {
							continue;
						}
						this.landings.push({
							startTime: new Date(potentialLanding.startTime),
							endTime: new Date(current.time),
							startAltitude: potentialLanding.startAltitude,
							startMslAltitude: potentialLanding.startMslAltitude,
							endAltitude: current.altitude,
							endMslAltitude: current.mslAltitude ?? current.altitude,
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							speed: current.speed,
							duration: totalTime,
							averageDescentRate: totalAltitudeChange / totalTime,
							averageDescentRateFPM: (totalAltitudeChange / totalTime) * 196.85,
							location: {
								latitude: current.latitude,
								longitude: current.longitude,
							},
						});
					}
				}
				potentialLanding = null;
				consecutiveDescents = 0;
			}
		}

		return this.landings;
	}
}
